import { MutationTree } from 'vuex';

import { expandEvents } from '@/utils/rrule-expander';
import {
  EventStateI,
  EventI,
  GuestI,
  RSVPResponseI,
} from './state';

export const mutations: MutationTree<EventStateI> = {
  setEvent(state: EventStateI, payload: EventI) {
    state.event = payload;
    state.eventTemplateInitialized = false;
  },
  setDetailDate(state: EventStateI, date: string) {
    state.detailDate = date;
  },
  setEventsLoading(state: EventStateI, loading: boolean) {
    state.loading = loading;
  },
  setEvents(
    state: EventStateI,
    payload: { events: EventI[]; rangeKey: string; startDate?: number; endDate?: number },
  ) {
    const { events, startDate, endDate } = payload;
    let finalEvents = events;
    if (startDate && endDate) {
      finalEvents = expandEvents(events, startDate, endDate);
    }

    state.events = finalEvents;
    state.loadedRangeKey = payload.rangeKey;
    state.loading = false;
  },
  clearEvents(state: EventStateI, rangeKey = '') {
    state.events = [];
    state.loadedRangeKey = rangeKey;
    state.loading = false;
  },
  deleteEvent(state: EventStateI, eventId: string | number) {
    state.events = state.events.filter((event) => event.id !== eventId);
  },
  addEvent(state: EventStateI, payload: EventI) {
    state.events.push(payload);
  },
  updateEvent(state: EventStateI, payload: EventI) {
    const payloadBaseId = String(payload.id).split('_')[0];
    const index = state.events.findIndex((event) => {
      const eventBaseId = String(event.id).split('_')[0];
      return eventBaseId === payloadBaseId;
    });
    if (index !== -1) {
      state.events.splice(index, 1, payload);
    }
  },
  addEventGuests(state: EventStateI, payload: { eventId: string | number; guests: GuestI[] }) {
    state.events = state.events.map((event) => {
      // Use loosely equal comparison because event.id could be a string '10' and eventId a number 10, or it might have _instance attached.
      const eventBaseId = String(event.id).split('_')[0];
      const payloadBaseId = String(payload.eventId).split('_')[0];
      if (eventBaseId === payloadBaseId) {
        return {
          ...event,
          guests: [...(event.guests || []), ...payload.guests],
        };
      }
      return event;
    });
  },
  removeEventGuest(state: EventStateI, payload: { eventId: string | number; guestId: string }) {
    state.events = state.events.map((event) => {
      const eventBaseId = String(event.id).split('_')[0];
      const payloadBaseId = String(payload.eventId).split('_')[0];
      if (eventBaseId === payloadBaseId && event.guests) {
        return {
          ...event,
          guests: event.guests.filter((guest) => guest.id !== payload.guestId),
        };
      }
      return event;
    });
  },
  replaceEventGuests(state: EventStateI, payload: { eventId: string | number; guests: GuestI[] }) {
    state.events = state.events.map((event) => {
      const eventBaseId = String(event.id).split('_')[0];
      const payloadBaseId = String(payload.eventId).split('_')[0];
      if (eventBaseId === payloadBaseId) {
        return {
          ...event,
          guests: payload.guests,
        };
      }
      return event;
    });
  },

  setRSVPResponse(state: EventStateI, payload: RSVPResponseI) {
    state.rsvpResponse = payload;
  },

  initializeEventFromTemplate(state: EventStateI, payload: Partial<EventI>) {
    state.event = {
      ...state.event,
      ...payload,
    };
    state.eventTemplateInitialized = true;
  },

  clearEventTemplate(state: EventStateI) {
    state.event = {
      id: 0,
      calendarId: 0,
      userId: 0,
      title: '',
      description: '',
      startTime: 0,
      endTime: 0,
      createdAt: 0,
      updatedAt: 0,
      recurrenceRule: '',
      icon: '',
      utc: '',
    };
    state.eventTemplateInitialized = false;
  },
};
